import React, { useEffect, useRef, useState } from "react";
import { ReactSVG } from "react-svg";
import { ChevronDown } from "lucide-react";
import copyIcon from "../../../Asset/copy-icon.svg";
import deleteIcon from "../../../Asset/delete-icon.svg";
import promptsIcon from "../../../Asset/Prompts.svg";

const TranscriptHistoryPanel = ({
  items,
  promptOptions,
  onApplyPrompt,
  onDelete,
  onCopy,
}) => {
  const [expandedIds, setExpandedIds] = useState({});
  const [openPromptMenuById, setOpenPromptMenuById] = useState({});
  const [applyingById, setApplyingById] = useState({});
  const menuRefs = useRef({});

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    const onDocumentClick = (event) => {
      setOpenPromptMenuById((prev) => {
        const next = { ...prev };
        let changed = false;
        Object.keys(next).forEach((id) => {
          if (!next[id]) return;
          const host = menuRefs.current[id];
          if (host && !host.contains(event.target)) {
            next[id] = false;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    };
    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, []);

  const handleApply = async (id, prompt) => {
    if (!prompt) return;
    setApplyingById((prev) => ({ ...prev, [id]: true }));
    setOpenPromptMenuById((prev) => ({ ...prev, [id]: false }));
    try {
      await onApplyPrompt?.(id, prompt);
    } finally {
      setApplyingById((prev) => ({ ...prev, [id]: false }));
    }
  };

  if (!items?.length) return null;

  return (
    <div className="rounded-3xl border border-[#D6ECFB] bg-white p-4">
      <div className="flex items-center gap-2 mb-4">
        <div className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] px-4 py-1.5 text-white text-lg font-semibold">
          <span>History</span>
          <span className="inline-flex items-center justify-center rounded-full bg-white/20 text-xs px-2 py-0.5">
            {items.length}
          </span>
        </div>
      </div>

      <div className="flex items-stretch gap-4 overflow-x-auto pb-2 custom-scrollbar">
        {items.map((item) => {
          const expanded = Boolean(expandedIds[item.id]);
          const words = String(item.content || "")
            .trim()
            .split(/\s+/)
            .filter(Boolean);
          const isLong = words.length > 50;
          const applying = Boolean(applyingById[item.id]);
          const menuOpen = Boolean(openPromptMenuById[item.id]);
          const previewText = isLong ? `${words.slice(0, 50).join(" ")}...` : item.content;

          return (
            <div
              key={item.id}
              className="shrink-0 w-[300px] min-h-[320px] rounded-[16px] border border-[rgba(58,177,246,0.10)] bg-[#F8FBFF] p-4 flex flex-col"
            >
              <h3 className="text-[#000] font-inter text-[14px] not-italic font-semibold leading-[19.6px]">
                {item.title || "Untitled"}
              </h3>

              <div className="mt-3 flex-1 min-h-0 flex flex-col">
                <div className="flex-1 min-h-0 overflow-y-auto pr-1 custom-scrollbar">
                  <p className="text-[#000] font-inter text-[12px] not-italic font-normal leading-[18.6px] tracking-[-0.12px] whitespace-pre-wrap">
                    {expanded || !isLong ? item.content : previewText}
                  </p>
                </div>

                {isLong && (
                  <button
                    type="button"
                    onClick={() => toggleExpanded(item.id)}
                    className="mt-2 text-sm text-[#3AB1F6] font-medium text-left"
                  >
                    {expanded ? "Show less" : "Show more"}
                  </button>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-[#D6ECFB]">
                <div className="text-xs text-[#4B5563]">
                  {item.wordCount} {item.wordCount === 1 ? "word" : "words"}
                </div>

                <div className="mt-3 flex items-center gap-2">
                <div
                  className="relative"
                  ref={(node) => {
                    menuRefs.current[item.id] = node;
                  }}
                >
                  <button
                    type="button"
                    onClick={() =>
                      setOpenPromptMenuById((prev) => ({ ...prev, [item.id]: !prev[item.id] }))
                    }
                    disabled={applying || !promptOptions?.length}
                    className="inline-flex items-center gap-1 rounded-lg bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] text-white px-2 py-1 text-xs font-medium disabled:opacity-60"
                  >
                    <ReactSVG
                      src={promptsIcon}
                      beforeInjection={(svg) => svg.setAttribute("class", "w-3 h-3")}
                      className="w-3 h-3"
                    />
                    {applying ? "Applying..." : "Prompts"}
                    <ChevronDown className="size-3" />
                  </button>

                  {menuOpen && (
                    <div className="absolute z-30 bottom-full mb-1 w-48 max-h-[144px] overflow-y-auto rounded-lg border border-[#D6ECFB] bg-[#F1FAFF] shadow-md custom-scrollbar">
                      {promptOptions.map((prompt) => (
                        <button
                          key={prompt.id}
                          type="button"
                          onClick={() => handleApply(item.id, prompt)}
                          style={{ "--bg": prompt?.bgColor }}
                          className="group w-full flex items-center gap-2 text-left px-2.5 py-2 text-xs font-inter text-[#363636] hover:bg-[#3AB1F6] hover:text-white transition"
                        >
                          <span className="p-1.5 rounded-md transition bg-[var(--bg)] group-hover:bg-white">
                            <ReactSVG
                              src={prompt.icon}
                              beforeInjection={(svg) => svg.setAttribute("class", "w-3 h-3")}
                              className="w-3 h-3"
                            />
                          </span>
                          {prompt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => onCopy?.(item.content)}
                  className="inline-flex items-center gap-1 rounded-lg bg-[#F2FAFF] text-[#2196F3] px-2 py-1 text-xs font-medium"
                >
                  <ReactSVG
                    src={copyIcon}
                    beforeInjection={(svg) => svg.setAttribute("class", "w-3 h-3")}
                    className="w-3 h-3"
                  />
                  Copy
                </button>

                <button
                  type="button"
                  onClick={() => onDelete?.(item.id)}
                  className="inline-flex items-center gap-1 rounded-lg bg-[#FFF3F3] text-[#FF4D4F] px-2 py-1 text-xs font-medium"
                >
                  <ReactSVG
                    src={deleteIcon}
                    beforeInjection={(svg) => svg.setAttribute("class", "w-3 h-3")}
                    className="w-3 h-3"
                  />
                  Delete
                </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TranscriptHistoryPanel;
